// Coin formatting — ladder per the economy config (100 copper = 1 silver,
// 100 silver = 1 gold). Amounts are stored in copper (cp) everywhere; these
// helpers only shape the display.

export const CP_PER_SILVER = 100;
export const CP_PER_GOLD = 100 * CP_PER_SILVER;

/** Compact one-number display for tight spots (nav widget, chips): "250c" · "3.5s" · "1.2g". */
export function formatCoinCompact(cp: number): string {
  const abs = Math.abs(cp);
  const sign = cp < 0 ? '−' : '';
  if (abs >= CP_PER_GOLD) return `${sign}${trim(abs / CP_PER_GOLD)}g`;
  if (abs >= CP_PER_SILVER) return `${sign}${trim(abs / CP_PER_SILVER)}s`;
  return `${sign}${abs}c`;
}

/** Full breakdown for the wallet header: "1 gold · 20 silver · 50 copper" (skips zero parts). */
export function formatCoinFull(cp: number): string {
  const abs = Math.abs(cp);
  const gold = Math.floor(abs / CP_PER_GOLD);
  const silver = Math.floor((abs % CP_PER_GOLD) / CP_PER_SILVER);
  const copper = abs % CP_PER_SILVER;
  const parts: string[] = [];
  if (gold > 0) parts.push(`${gold} gold`);
  if (silver > 0) parts.push(`${silver} silver`);
  if (copper > 0 || parts.length === 0) parts.push(`${copper} copper`);
  return (cp < 0 ? '−' : '') + parts.join(' · ');
}

export interface CoinPart {
  denom: 'gold' | 'silver' | 'copper';
  value: number;
}

/**
 * The denominations an amount ACTUALLY has — the same split `formatCoinFull`
 * speaks, handed back as data so an icon can be drawn beside each figure.
 *
 * ⚠️ Deliberately NOT what `<CoinAmount>`'s full mode prints. That one always
 * shows all three, because on a BALANCE a "0" next to the gold coin reads as an
 * aspiration. A reward card is the opposite case: "+ 0 gold 2 silver 50 copper"
 * makes a piece of good news start with a nothing. Zero parts are dropped here,
 * and an amount of 0 still says "0 copper" rather than nothing at all.
 */
export function coinParts(cp: number): CoinPart[] {
  const abs = Math.abs(cp);
  const gold = Math.floor(abs / CP_PER_GOLD);
  const silver = Math.floor((abs % CP_PER_GOLD) / CP_PER_SILVER);
  const copper = abs % CP_PER_SILVER;
  const parts: CoinPart[] = [];
  if (gold > 0) parts.push({ denom: 'gold', value: gold });
  if (silver > 0) parts.push({ denom: 'silver', value: silver });
  if (copper > 0 || parts.length === 0) parts.push({ denom: 'copper', value: copper });
  return parts;
}

function trim(n: number): string {
  return n.toFixed(1).replace(/\.0$/, '');
}
